import {
	Badge,
	DotMatrix,
	SearchableSelect,
	SearchableSelectOption
} from '@clickguard/design-system'
import { useEffect, useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import { Rules } from 'services/user/rules'
import { useAssetStore } from 'store'
import {
	ASN_PROBLEM,
	formatAsn,
	formatAsnWithName,
	parseAsn,
	selectedAsnNames
} from './network-values'

export const NETWORK_SUGGESTIONS_KEY = 'custom-rules-network-suggestions'

const useNetworkSuggestions = () => {
	const asset = useAssetStore((state) => state.asset)
	const demoMode = useAssetStore((state) => state.demoMode)
	const accountId = demoMode ? 'workspace-demo' : asset?.account_id
	const assetId = demoMode ? 'website-demo' : asset?.id
	return useQuery(
		[NETWORK_SUGGESTIONS_KEY, accountId, assetId],
		() =>
			Rules.getNetworkSuggestions({ accountId: accountId!, assetId: assetId! }),
		{ enabled: !!accountId && !!assetId, staleTime: 5 * 60 * 1000 }
	)
}

const MENU_NOTE =
	'px-3 py-4 text-center text-base text-[rgb(var(--cg-text-tertiary))]'

interface NetworkPickerProps {
	kind: 'isp' | 'asn'
	values: string[]
	names?: Record<string, string>
	onChange: (values: string[], names?: Record<string, string>) => void
	placeholder?: string
	ariaLabel: string
	menuPortal: HTMLElement | boolean
	className?: string
}

// The field and chips are siblings inside a wrapping flex row.
export const NetworkPicker = ({
	kind,
	values,
	names,
	onChange,
	placeholder,
	ariaLabel,
	menuPortal,
	className
}: NetworkPickerProps) => {
	const { data, isLoading, isError, refetch } = useNetworkSuggestions()
	const [problem, setProblem] = useState('')
	const noun = kind === 'isp' ? 'ISP' : 'ASN'
	const suggestions = useMemo(
		() => (kind === 'isp' ? data?.isps : data?.asns) ?? [],
		[data, kind]
	)
	const has = (value: string) =>
		values.some(
			(entry) => entry.trim().toLowerCase() === value.trim().toLowerCase()
		)
	const knownNames = useMemo(() => {
		const merged = { ...names }
		if (kind === 'asn')
			suggestions.forEach((suggestion) => {
				if (suggestion.name) merged[suggestion.value] = suggestion.name
			})
		return merged
	}, [kind, names, suggestions])

	useEffect(() => {
		if (kind !== 'asn') return
		const selected = selectedAsnNames(
			values.map(parseAsn).filter((value): value is number => value !== null),
			knownNames
		)
		const keys = Object.keys(selected)
		if (
			keys.length !== Object.keys(names ?? {}).length ||
			keys.some((key) => selected[key] !== names?.[key])
		) {
			onChange(values, selected)
		}
	}, [kind, values, names, knownNames, onChange])

	const options: SearchableSelectOption[] = suggestions
		.map((suggestion) => {
			const display =
				kind === 'asn' ? formatAsn(Number(suggestion.value)) : suggestion.value
			const name = kind === 'asn' ? suggestion.name : undefined
			return {
				value: display,
				label: name ? (
					<>
						{display}{' '}
						<span className="text-[rgb(var(--cg-text-tertiary))]">
							· {name}
						</span>
					</>
				) : (
					display
				),
				searchText: [suggestion.value, display, name].filter(Boolean).join(' ')
			}
		})
		.filter((option) => !has(option.value))

	const change = (next: string[]) =>
		onChange(
			next,
			kind === 'asn'
				? selectedAsnNames(
						next
							.map(parseAsn)
							.filter((value): value is number => value !== null),
						knownNames
				  )
				: undefined
		)
	const add = (typed: string) => {
		const number = kind === 'asn' ? parseAsn(typed) : null
		if (kind === 'asn' && number === null) {
			setProblem(ASN_PROBLEM)
			return
		}
		const value = number === null ? typed.trim() : formatAsn(number)
		setProblem('')
		if (value && !has(value)) change([...values, value])
	}
	const canAdd = (typed: string) => {
		const query = typed.trim().toLowerCase()
		if (!query) return false
		const number = kind === 'asn' ? parseAsn(typed) : null
		const value = number === null ? query : formatAsn(number).toLowerCase()
		if (
			has(value) ||
			options.some((option) => option.value.toLowerCase() === value)
		)
			return false
		if (kind === 'asn' && number === null) {
			return !options.some((option) =>
				option.searchText?.toLowerCase().includes(query)
			)
		}
		return true
	}

	return (
		<>
			<SearchableSelect
				className={className}
				data-rule-field={kind}
				fullWidth
				options={options}
				filter={(option, query) => {
					const number = kind === 'asn' ? parseAsn(query) : null
					return (
						(number !== null && option.value === formatAsn(number)) ||
						(option.searchText ?? option.value)
							.toLowerCase()
							.includes(query.trim().toLowerCase())
					)
				}}
				placeholder={placeholder}
				searchPlaceholder={placeholder}
				ariaLabel={ariaLabel}
				clearOnSelect
				onValueChange={add}
				onCreate={add}
				canCreate={canAdd}
				createLabel={(typed) => {
					const number = kind === 'asn' ? parseAsn(typed) : null
					return `Add “${number === null ? typed.trim() : formatAsn(number)}”`
				}}
				renderEmpty={(query) => (
					<div className={MENU_NOTE}>
						{isLoading ? (
							<div className="flex items-center justify-center gap-2">
								<DotMatrix size="xs" label={`Loading ${noun}s`} />
								Loading the last 30 days
							</div>
						) : isError ? (
							'Suggestions are unavailable. You can still add a value.'
						) : query ? (
							`“${query}” is already added`
						) : suggestions.length ? (
							`Every ${noun} from the last 30 days is added`
						) : (
							`No ${noun}s detected in the last 30 days`
						)}
					</div>
				)}
				portal={menuPortal}
			/>
			{values.length > 0 && (
				<div className="flex w-full flex-wrap items-center gap-1.5">
					{values.map((value) => {
						const number = kind === 'asn' ? parseAsn(value) : null
						return (
							<Badge
								key={value}
								variant="brand"
								size="sm"
								className="max-w-full !h-auto min-h-[20px]"
								onClose={() => {
									setProblem('')
									change(values.filter((entry) => entry !== value))
								}}
							>
								<span className="min-w-0 whitespace-normal break-words py-0.5">
									{number === null
										? value
										: formatAsnWithName(number, knownNames[String(number)])}
								</span>
							</Badge>
						)
					})}
				</div>
			)}
			{isError && (
				<p
					role="status"
					className="w-full text-base text-[rgb(var(--cg-text-secondary))]"
				>
					Couldn’t load {noun} suggestions. You can still add a value.{' '}
					<button
						type="button"
						className="underline"
						onClick={() => void refetch()}
					>
						Retry
					</button>
				</p>
			)}
			{problem && (
				<p
					role="alert"
					className="w-full text-base text-[rgb(var(--cg-danger))]"
				>
					{problem}
				</p>
			)}
		</>
	)
}
